// Lookups over the resolved view configuration (GET /processes/view-config).
// The server decides what each database shows and how
// (backend/services/viewConfig.js); these helpers only read it, with a
// neutral answer for a type the configuration doesn't list yet.

// Used until the configuration arrives, or if the server can't be reached.
// They mirror the server's defaults, which remain the source of truth.
export const FALLBACK_SETTINGS = Object.freeze({
  "search.pageSize": 10,
  "search.sortFields": ["name"],
  "search.defaultType": null,
  "quickSearch.limit": 20,
  "explorer.nodeLabelLength": 30,
  "card.descriptionLength": 180,
  "card.fieldLimit": 3,
  "hover.fieldLimit": 4,
  // Mirrors backend/services/viewConfig.js DEFAULT_MAP_FIRST_TYPES: the types
  // whose live map is shown first (their stored diagrams are still offered
  // beside it), used until the server's own configuration (which may
  // customise this list) arrives.
  "diagram.mapFirstTypes": [
    "E2E-Domain",
    "Process Group",
    "Process Cluster",
    "E2E-Scenario Cluster",
    "Blueprint Map",
    "Work Package Library",
    "Functional Process Map",
    "E2E-Map",
  ],
  // Mirrors the backend's own defaults for how a stored picture is shown and
  // how a map is ordered (backend/services/viewConfig.js SETTINGS).
  "diagram.mapBeforeExports": true,
  // An object with parts and no drawing of its own is shown, downloaded and
  // rendered as the map we draw; the file it was imported with stays beside
  // it under "Source picture".
  "diagram.mapAsSvg": true,
  // ...and where we draw the object, that imported file is not offered as a
  // view at all. Off by default: an admin whose exports are good switches it
  // back on per database (backend/services/viewConfig.js SETTINGS).
  "diagram.showSourcePicture": false,
  "diagram.generatedMap": true,
  "diagram.generatedMaxParts": 48,
  "diagram.mapFollowsFlow": true,
  "diagram.flowOrderMaxKb": 2048,
  "diagram.enhanceSvg": true,
  "diagram.svgMinLabelSize": 11,
  "diagram.svgFlatFillLightness": 92,
  "diagram.formatLabel.structure": "Map",
  // Named after the type itself, as on the server (backend/config/diagramFormats.js).
  "diagram.formatLabel.bpmn": "BPMN",
  // A format whose name isn't simply its initials needs its default here, or
  // it reads as MERMAID until the server's configuration arrives.
  "diagram.formatLabel.mermaid": "Mermaid",
  // What an imported picture is called where our own drawing is the object's
  // picture (components/diagram/formatRoute.js SOURCE).
  "diagram.formatLabel.source": "Source picture",
  ignoredAttributes: [
    "GUID",
    "hasAssignment",
    "inTrial",
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "archivedAt",
    "archivedBy",
    "supersededBy",
    "BPMN",
    "SVG",
    "PNG",
    "PDF",
    "Mermaid",
    "content",
  ],
  // Mirrors backend/config/contentModel.js DEFAULT_EDITABLE_FIELDS.
  "editor.fields": ["name", "Description", "Outcome", "Scope", "Advisory_Notes", "Prerequisite", "Business_Rules"],
});

// Mirrors backend/config/diagramFormats.js DIAGRAM_TYPE_LABELS: a diagram
// node reads as its own type until an admin renames it.
const FALLBACK_TYPE_LABELS = Object.freeze({});
// Mirrors backend/config/diagramFormats.js DIAGRAM_NODE_LABELS: diagram
// nodes are what an object is drawn as, so no view starts with them - not the
// explorer, advanced or quick search, nor structure maps. A diagram opens
// through "Open diagram", which no view flag touches, and an admin can switch
// any of them on per database.
const FALLBACK_DIAGRAM_TYPES = Object.freeze(["BPMN", "SVG", "PNG", "PDF", "Mermaid", "Diagram"]);
// Mirrors backend/config/contentModel.js PROTECTED_FIELDS, so the content
// editor never offers these for editing even before a database's own
// configuration has loaded.
const FALLBACK_PROTECTED_FIELDS = Object.freeze([
  "GUID",
  "Type",
  "inTrial",
  "hasAssignment",
  "createdAt",
  "createdBy",
  "updatedAt",
  "updatedBy",
  "archivedAt",
  "archivedBy",
  "supersededBy",
]);
const FALLBACK_PALETTE = ["#1a1a2e", "#2f5bd3", "#0f7b43", "#8f8a1d", "#a8431f", "#5b3fa3", "#0e7490", "#9d2a5c"];
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// The colour the server gives a type by default (same palette, same hash).
export const fallbackColour = (type = "") => {
  const text = String(type || "");
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return FALLBACK_PALETTE[hash % FALLBACK_PALETTE.length];
};

export const EMPTY_VIEW_CONFIG = Object.freeze({
  loaded: false,
  types: [],
  relationships: [],
  fields: { card: {}, hover: {}, quickSearch: {} },
  settings: FALLBACK_SETTINGS,
  protectedFields: FALLBACK_PROTECTED_FIELDS,
});

export const withDefaults = (content) => ({
  ...EMPTY_VIEW_CONFIG,
  ...(content || {}),
  fields: { ...EMPTY_VIEW_CONFIG.fields, ...(content?.fields || {}) },
  settings: { ...FALLBACK_SETTINGS, ...(content?.settings || {}) },
  loaded: true,
});

export const objectTypeOf = (item) => item?.Type || item?.type || "";

// A Map from objectType to its configured type info, built once per config
// object (types don't change within one configuration) and reused for every
// lookup, so a screen with many nodes/cards doesn't re-scan the type list
// per lookup.
const typeIndexCache = new WeakMap();

export function typeIndex(config) {
  if (!config || typeof config !== "object") return new Map();
  const cached = typeIndexCache.get(config);
  if (cached) return cached;
  const map = new Map((config.types || []).map((t) => [t.objectType, t]));
  typeIndexCache.set(config, map);
  return map;
}

export function typeInfo(config, objectType) {
  const found = typeIndex(config).get(objectType);
  if (found) return found;
  const name = objectType || "";
  const isDiagram = FALLBACK_DIAGRAM_TYPES.includes(name);
  return {
    objectType: name,
    label: (hasOwn(FALLBACK_TYPE_LABELS, name) && FALLBACK_TYPE_LABELS[name]) || name || "Object",
    colour: fallbackColour(name),
    icon: isDiagram ? "diagram" : "object",
    inExplorer: !isDiagram,
    inSearch: !isDiagram,
    inQuickSearch: !isDiagram,
    inStructure: !isDiagram,
    isDiagram,
  };
}

export const typeLabel = (config, objectType) => typeInfo(config, objectType).label;
export const typeColour = (config, objectType) => typeInfo(config, objectType).colour;

// A wash of a type's own colour, for a screen that tints something with it -
// the colour always comes from the configuration above, never from anything
// hard-coded per customer or per type. A colour written in any other notation
// simply washes in the brand's ink.
//
// It lives with the drawing's own geometry (components/diagram/generatedLayout.js),
// because the server writes the same pictures with it and that module is the
// one file both sides read. Everything here keeps importing it from here.
export { tint } from "../components/diagram/generatedLayout";

// Relationships are configured beside the object types (Views ->
// Relationships): a renamed relationship reads by its new name wherever it is
// shown, and an unconfigured one by its type.
const relationshipIndexCache = new WeakMap();

export function relationshipIndex(config) {
  if (!config || typeof config !== "object") return new Map();
  const cached = relationshipIndexCache.get(config);
  if (cached) return cached;
  const map = new Map((config.relationships || []).map((r) => [r.type, r]));
  relationshipIndexCache.set(config, map);
  return map;
}

// A relationship nobody has renamed yet is still read by people, not by a
// database: `ASSIGNED_TO` is shown as "Assigned to". Only the shape a storage
// name has - all capitals, underscores between the words - is rewritten;
// anything already written for a reader ("Belongs to", "assignedTo") is left
// exactly as it is, and an admin's own label always wins over both.
export const humanRelationship = (type) => {
  const name = String(type || "").trim();
  if (!name) return "";
  if (/[a-z]/.test(name)) return name.replace(/_/g, " ");
  const words = name.replace(/_/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export function relationshipInfo(config, type) {
  const found = relationshipIndex(config).get(type);
  // The server lists every relationship it discovered and gives one nobody has
  // configured a label that is simply its own raw name. Read straight back out
  // that made the explorer shout "STARTS_AT" at a reader while the structure
  // map, which humanises the same name itself, said "Starts at" about the very
  // same edge. A label equal to the name is nobody's choice, so it is read the
  // way the map reads it; an admin's own label is a choice and always wins.
  if (found) return found.label === found.type ? { ...found, label: humanRelationship(found.type) || found.type } : found;
  const name = type || "";
  return { type: name, label: humanRelationship(name) || "Related", inExplorer: true, direction: "both" };
}

export const relationshipLabel = (config, type) => relationshipInfo(config, type).label;

export function setting(config, key) {
  const value = config?.settings?.[key];
  return value === undefined ? FALLBACK_SETTINGS[key] : value;
}

// Types a view shows, in the order the server gives (the admin's order).
export const typesIn = (config, flag) => (config?.types || []).filter((t) => t[flag] !== false);

export const displayValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(displayValue).filter(Boolean).join(", ");
  if (typeof value === "object") return "";
  return String(value);
};

// The name and the type are the title of every card, never fields.
const TITLE_KEYS = ["name", "Type"];

// Never a field, regardless of what the admin configured: `formats` (and any
// future key in the same spirit) is added client-side by AdvanceSearch, not
// stored content; `URL` already has its own link button on the card; `format`
// and `url` are the generic (:Diagram { format, content | url }) node's own
// storage keys, not something a reader should see as an attribute.
const NEVER_FIELDS = new Set(["formats", "URL", "format", "url"]);

// Which attributes one view shows for one object: the admin's fields for its
// type, where the object holds a value; else everything it carries.
// Under a group's view (config.view set) the view's list is a whitelist: an
// empty list means the name alone, never "everything".
export function fieldsFor(config, view, item) {
  const has = (key) => displayValue(item?.[key]).trim() !== "";
  const configured = config?.fields?.[view]?.[objectTypeOf(item)];
  if (Array.isArray(configured) && (configured.length > 0 || config?.view)) {
    return configured.filter((key) => !NEVER_FIELDS.has(key) && has(key));
  }
  const hidden = new Set([...(setting(config, "ignoredAttributes") || []), ...TITLE_KEYS, ...NEVER_FIELDS]);
  const keys = Object.keys(item || {}).filter((key) => key.charAt(0) !== "_" && !hidden.has(key) && has(key));
  const rest = keys.filter((key) => key !== "Description").sort((a, b) => a.localeCompare(b));
  return keys.includes("Description") ? ["Description", ...rest] : rest;
}

// An attribute's own name, read as words. An importer writes for a machine, so
// `match_method` and `matchMethod` are the same attribute to a reader and both
// should say "Match method".
//
// A capital **that begins a word** - one with a lowercase letter after it and
// a lowercase letter or digit before it - starts a new word. A run of capitals
// does not: `SAPScope` and `GUID` are acronyms somebody chose, and taking them
// apart would be rewriting what a person wrote rather than reading what a
// machine did.
export const attributeLabel = (key) => {
  const words = String(key || "")
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])(?=[a-z])/g, (match, before, capital) => `${before} ${capital.toLowerCase()}`)
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const formatLabel = (config, format) => setting(config, `diagram.formatLabel.${format}`) || String(format || "").toUpperCase();

export const shortName = (name, length) => {
  if (typeof name !== "string") return name;
  return length > 0 && name.length > length ? `${name.slice(0, length)}…` : name;
};

// "Domain: 2 · Flow: 1" - counts per type under the admin's labels.
export function describeCounts(config, items = []) {
  const counts = new Map();
  items.forEach((item) => {
    const type = objectTypeOf(item);
    counts.set(type, (counts.get(type) || 0) + 1);
  });
  if (counts.size === 0) return "No objects";
  return [...counts.entries()].map(([type, count]) => `${typeLabel(config, type)}: ${count}`).join(" · ");
}
